import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import {
	AdminPointOfInterestTypeDto,
	AdminPointOfInterestTypesListResponse,
	AdminPointOfInterestTypesService,
} from '../../api/admin-point-of-interest-types.service';

@Component({
	selector: 'of-admin-point-of-interest-types-page',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatButtonModule,
		MatCardModule,
		MatProgressBarModule,
		MatFormFieldModule,
		MatInputModule,
	],
	template: `
		<div class="page">
			<mat-card class="card" *ngIf="vm$ | async as vm; else loading">
				<h2>Admin · POI Types</h2>
				<p class="muted">Manage point-of-interest type metadata (global).</p>

				<div class="actions" style="justify-content: flex-end;">
					<button mat-button (click)="refresh()">Refresh</button>
				</div>

				<div style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);">
					<div style="font-weight: 600; margin-bottom: 8px;">New POI Type</div>
					<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
						<mat-form-field appearance="fill">
							<mat-label>Name</mat-label>
							<input matInput [(ngModel)]="createModel.name" name="createName" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Category</mat-label>
							<input matInput [(ngModel)]="createModel.category" name="createCategory" />
						</mat-form-field>
						<mat-form-field appearance="fill" style="grid-column: 1 / -1;">
							<mat-label>Description</mat-label>
							<textarea matInput rows="3" [(ngModel)]="createModel.description" name="createDescription"></textarea>
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Travel Time Modifier</mat-label>
							<input matInput type="number" [(ngModel)]="createModel.travelTimeModifier" name="createTravelTimeModifier" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Default Attitude</mat-label>
							<input matInput [(ngModel)]="createModel.defaultAttitude" name="createDefaultAttitude" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Default Loot Profile</mat-label>
							<input matInput [(ngModel)]="createModel.defaultLootProfile" name="createDefaultLootProfile" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Encounter Bias</mat-label>
							<input matInput [(ngModel)]="createModel.encounterBias" name="createEncounterBias" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Color Token</mat-label>
							<input matInput [(ngModel)]="createModel.colorToken" name="createColorToken" />
						</mat-form-field>
						<mat-form-field appearance="fill">
							<mat-label>Image Asset ID</mat-label>
							<input matInput [(ngModel)]="createModel.imageAssetId" name="createImageAssetId" />
						</mat-form-field>
					</div>
					<div style="display:flex; justify-content:flex-end; margin-top: 8px;">
						<button mat-flat-button color="primary" (click)="create()" [disabled]="creating || !createModel.name">
							Create
						</button>
					</div>
				</div>

				<div
					*ngFor="let t of vm.pointOfInterestTypes"
					style="padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.08);"
				>
					<div style="display:flex; gap: 12px; align-items: center;">
						<div style="min-width: 0; flex: 1 1 auto;">
							<div style="font-weight: 600;">{{ t.name }}</div>
							<div class="muted" style="font-size: 13px;">
								{{ t.category ?? '—' }} · {{ t.id }}
							</div>
						</div>
						<button mat-button (click)="beginEdit(t)" *ngIf="editingId !== t.id">Edit</button>
						<button mat-button color="warn" (click)="remove(t.id)">Delete</button>
					</div>

					<div *ngIf="editingId === t.id" style="margin-top: 12px;">
						<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
							<mat-form-field appearance="fill">
								<mat-label>Name</mat-label>
								<input matInput [(ngModel)]="editModel.name" name="editName" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Category</mat-label>
								<input matInput [(ngModel)]="editModel.category" name="editCategory" />
							</mat-form-field>
							<mat-form-field appearance="fill" style="grid-column: 1 / -1;">
								<mat-label>Description</mat-label>
								<textarea matInput rows="3" [(ngModel)]="editModel.description" name="editDescription"></textarea>
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Travel Time Modifier</mat-label>
								<input matInput type="number" [(ngModel)]="editModel.travelTimeModifier" name="editTravelTimeModifier" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Default Attitude</mat-label>
								<input matInput [(ngModel)]="editModel.defaultAttitude" name="editDefaultAttitude" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Default Loot Profile</mat-label>
								<input matInput [(ngModel)]="editModel.defaultLootProfile" name="editDefaultLootProfile" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Encounter Bias</mat-label>
								<input matInput [(ngModel)]="editModel.encounterBias" name="editEncounterBias" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Color Token</mat-label>
								<input matInput [(ngModel)]="editModel.colorToken" name="editColorToken" />
							</mat-form-field>
							<mat-form-field appearance="fill">
								<mat-label>Image Asset ID</mat-label>
								<input matInput [(ngModel)]="editModel.imageAssetId" name="editImageAssetId" />
							</mat-form-field>
						</div>

						<div style="display:flex; justify-content:flex-end; gap: 8px; margin-top: 8px;">
							<button mat-button (click)="cancelEdit()">Cancel</button>
							<button mat-flat-button color="primary" (click)="save()" [disabled]="saving || !editModel.name">
								Save
							</button>
						</div>
					</div>
				</div>
			</mat-card>
		</div>

		<ng-template #loading>
			<div class="page">
				<mat-card class="card">
					<mat-progress-bar mode="indeterminate"></mat-progress-bar>
				</mat-card>
			</div>
		</ng-template>
	`,
})
export class AdminPointOfInterestTypesPage {
	private readonly refresh$ = new BehaviorSubject<void>(undefined);
	vm$: Observable<AdminPointOfInterestTypesListResponse>;

	creating = false;
	saving = false;

	editingId: string | null = null;
	editModel: Partial<AdminPointOfInterestTypeDto> = {};

	createModel: {
		name: string;
		description: string;
		category: string;
		travelTimeModifier: number;
		defaultAttitude: string;
		defaultLootProfile: string;
		encounterBias: string;
		colorToken: string;
		imageAssetId: string;
	} = {
		name: '',
		description: '',
		category: '',
		travelTimeModifier: 1.0,
		defaultAttitude: '',
		defaultLootProfile: '',
		encounterBias: '',
		colorToken: '',
		imageAssetId: '',
	};

	constructor(private readonly admin: AdminPointOfInterestTypesService) {
		this.vm$ = this.refresh$.pipe(switchMap(() => this.admin.list()));
	}

	refresh() {
		this.refresh$.next();
	}

	beginEdit(row: AdminPointOfInterestTypeDto) {
		this.editingId = row.id;
		this.editModel = { ...row };
	}

	cancelEdit() {
		this.editingId = null;
		this.editModel = {};
	}

	create() {
		if (this.creating) return;
		if (!this.createModel.name.trim()) return;
		this.creating = true;
		this.admin
			.create({
				name: this.createModel.name.trim(),
				description: this.createModel.description || null,
				category: this.createModel.category || null,
				travelTimeModifier: this.createModel.travelTimeModifier,
				defaultAttitude: this.createModel.defaultAttitude || null,
				defaultLootProfile: this.createModel.defaultLootProfile || null,
				encounterBias: this.createModel.encounterBias || null,
				colorToken: this.createModel.colorToken || null,
				imageAssetId: this.createModel.imageAssetId || null,
			})
			.subscribe({
				next: () => {
					this.creating = false;
					this.createModel = {
						name: '',
						description: '',
						category: '',
						travelTimeModifier: 1.0,
						defaultAttitude: '',
						defaultLootProfile: '',
						encounterBias: '',
						colorToken: '',
						imageAssetId: '',
					};
					this.refresh();
				},
				error: () => {
					this.creating = false;
					this.refresh();
				},
			});
	}

	save() {
		if (!this.editingId || this.saving) return;
		if (!this.editModel.name?.trim()) return;
		this.saving = true;
		this.admin
			.update(this.editingId, {
				name: this.editModel.name.trim(),
				description: this.editModel.description ?? null,
				category: this.editModel.category ?? null,
				travelTimeModifier: this.editModel.travelTimeModifier ?? null,
				defaultAttitude: this.editModel.defaultAttitude ?? null,
				defaultLootProfile: this.editModel.defaultLootProfile ?? null,
				encounterBias: this.editModel.encounterBias ?? null,
				colorToken: this.editModel.colorToken ?? null,
				imageAssetId: this.editModel.imageAssetId ?? null,
			})
			.subscribe({
				next: () => {
					this.saving = false;
					this.cancelEdit();
					this.refresh();
				},
				error: () => {
					this.saving = false;
					this.refresh();
				},
			});
	}

	remove(id: string) {
		this.admin.delete(id).subscribe({
			next: () => {
				if (this.editingId === id) this.cancelEdit();
				this.refresh();
			},
			error: () => this.refresh(),
		});
	}
}
